<template>
  <f7-page name="seller-hub" @page:show="onShow">
    <f7-navbar :title="$t('sellerHub.title')" back-link>
      <f7-nav-right>
        <f7-button  small raised round fill href="/shop/create"  >{{ $t('sellerHub.openShop') }}</f7-button>
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <f7-block v-if="loadError" class="sel-error">
          <f7-icon f7="exclamationmark_triangle_fill" />
          <span>{{ loadError }}</span>
          <f7-button small round raised fill @click="load">{{ $t('common.retry') }}</f7-button>
        </f7-block>

        <div v-if="loading" class="text-align-center" style="padding: 32px 0">
          <f7-preloader />
        </div>

        <template v-else>
          <!-- Seller profile banner -->
          <f7-block  class="sel-profile no-margin no-padding">
            <f7-card v-if="profileSaved" outline class="sel-profile-card no-padding">
              <f7-card-content class="sel-profile-row">
                <div class="sel-profile-avatar">
                  <img v-if="profileSaved.logoUrl" :src="profileSaved.logoUrl" alt="" />
                  <f7-icon v-else f7="person_fill" size="22" />
                </div>
                <div class="sel-profile-info">
                  <div class="sel-profile-name">
                    {{ profileSaved.displayName || profileSaved.businessName }}
                  </div>
                  <div class="sel-profile-sub">
                    <span v-if="profileSaved.verified" class="sel-verified">
                      <f7-icon f7="checkmark_shield_fill" size="11" />
                      {{ $t('sellerHub.verified') }}
                    </span>
                    <span v-else class="sel-notverified">{{ $t('sellerHub.notVerified') }}</span>
                    <span v-if="profileSaved.sellerType">
                      · {{ profileSaved.sellerType === 'BUSINESS' ? $t('sellerHub.sellerTypeBusiness') : $t('sellerHub.sellerTypeIndividual') }}
                    </span>
                  </div>
                </div>
                <f7-button small fill round raised @click="editProfile">{{ $t('sellerHub.edit') }}</f7-button>
              </f7-card-content>
            </f7-card>
            <f7-card v-else outline no-padding class="sel-profile-card sel-profile-card--cta no-padding">
              <f7-card-content class="sel-profile-row">
                <div class="sel-profile-info">
                  <div class="sel-profile-name">{{ $t('sellerHub.profileCreate') }}</div>
                  <div class="sel-profile-sub">{{ $t('sellerHub.profileHint') }}</div>
                </div>
                <f7-button fill raised  round @click="openProfileForm">{{ $t('sellerHub.create') }}</f7-button>
              </f7-card-content>
            </f7-card>
          </f7-block>

          <!-- Shop upgrade threshold notification -->
          <f7-block v-if="suggestUpgrade" class="sel-upgrade no-padding no-margin">
            <f7-card outline class="sel-upgrade-card">
              <div class="sel-upgrade-icon">
                <f7-icon f7="store_2_fill" size="22" />
              </div>
              <div class="sel-upgrade-body">
                <div class="sel-upgrade-title">{{ $t('sellerHub.suggestUpgradeTitle') }}</div>
                <div class="sel-upgrade-text">{{ $t('sellerHub.suggestUpgradeText') }}</div>
              </div>
              <f7-button small round raised fill @click="goShopWizard">{{ $t('sellerHub.createShop') }}</f7-button>
            </f7-card>
          </f7-block>

          <!-- Actions -->
          <f7-block class=" grid grid-cols-2 grid-gap">
            <f7-button fill round large raised @click="openNewListing">
              <f7-icon f7="plus" size="16" />
              {{ $t('sellerHub.newListing') }}
            </f7-button>
            <f7-button outline round large raised @click="openProfileForm">
              {{ $t('sellerHub.profileButton') }}
            </f7-button>
          </f7-block>

          <f7-block-title>{{ $t('sellerHub.manage') }}</f7-block-title>
          <f7-list inset>
            <f7-list-item :title="$t('sellerHub.gotoListings')" link="#" @click="go('/sell/listings')">
              <template #media>
                <f7-icon f7="list_bullet" size="18" />
              </template>
            </f7-list-item>
            <f7-list-item :title="$t('sellerHub.gotoProfile')" link="#" @click="go('/sell/profile')">
              <template #media>
                <f7-icon f7="person_2_fill" size="18" />
              </template>
            </f7-list-item>
          </f7-list>

          <!-- My listings -->
          <f7-block-title>{{ $t('sellerHub.listingsTitle', { count: listings.length }) }}</f7-block-title>
          <f7-list v-if="listings.length" inset media-list>
            <f7-list-item
              v-for="l in listings"
              :key="l.id"
              :title="l.title"
              :subtitle="l.description"
              :after="formatPrice(l)"
              @click="openEdit(l)"
            >
              <template #media>
                <img v-if="cover(l)" class="listing-avatar" :src="cover(l)" :alt="l.title" />
                <span v-else class="listing-avatar sel-avatar-ph"><f7-icon f7="tag_fill" size="16" /></span>
              </template>
              <template #after="{ }">
                <div class="sel-listing-after">
                  <span class="sel-badge" :class="`sel-badge--${l.status}`">{{ statusLabel(l.status) }}</span>
                  <span class="sel-listing-price">{{ formatPrice(l) }}</span>
                  <div class="sel-listing-actions">
                    <f7-link
                      v-if="canSubmit(l)"
                      small
                      fill
                      @click.stop="submitListing(l)"
                    >
                      {{ $t('sellerHub.submit') }}
                    </f7-link>
                    <f7-link
                      v-if="canSubmitToGroup(l)"
                      small
                      @click.stop="go(`/sell/listings/${l.id}/submit`)"
                    >
                      {{ $t('sellerHub.toGroup') }}
                    </f7-link>
                    <f7-link v-if="l.groupId && submissionsByListing(l).length" small @click.stop="goMarketplace(l)">
                      {{ $t('sellerHub.viewGroup') }}
                    </f7-link>
                  </div>
                </div>
              </template>
            </f7-list-item>
          </f7-list>
          <f7-block v-else class="text-align-center text-color-gray sel-empty">
            {{ $t('sellerHub.emptyListings') }}
          </f7-block>

          <!-- Group marketplaces -->
          <f7-block-title>{{ $t('sellerHub.groupMarketplaces') }}</f7-block-title>
          <f7-list v-if="groups.length" inset media-list>
            <f7-list-item
              v-for="g in groups"
              :key="g.id"
              :title="g.name"
              :subtitle="g.category"
              link="#"
            
              @click="goMarketplaceGroup(g)"
              :after="$t('sellerHub.manageMarketplace')"
            >
             <template #text>
             <p v-html="g.description"></p>
              </template>

            <template #media>
              <img v-if="g.logoUrl" class="listing-avatar" :src="g.logoUrl" :alt="g.name" style="border-radius: 8px"  width="80" />
                <f7-icon v-else f7="person_3_fill" />
              </template>
             
            </f7-list-item>
          </f7-list>
          <f7-block v-else class="text-align-center text-color-gray sel-empty">
            {{ $t('sellerHub.noGroups') }}
          </f7-block>
        </template>
      </template>
    </AppLayout>

    <!-- Listing create/edit popup -->
    <AdaptiveSheet v-model:opened="listingSheetOpen" swipe-to-close :tablet-fullscreen="false" >
      <f7-page-content>
         <div class="card-header">
          <f7-block-title class="text-align-center">{{ listingSheetTitle }}</f7-block-title>
          <f7-link sheet-close popup-close style="right:8px"><f7-icon ios="f7:multiply" md="material:close" /></f7-link>
        </div>
        <f7-list inset strong no-hairlines>
          <f7-list-input
            type="text"
            :label="$t('sellerHub.itemName')"
            :value="listingForm.title"
            @input="listingForm.title = $event.target.value"
          />
          <f7-list-input
            type="textarea"
            resizable
            :label="$t('sellerHub.itemDesc')"
            :value="listingForm.description"
            @input="listingForm.description = $event.target.value"
          />
          <f7-list-input
            type="text"
            inputmode="numeric"
            :label="$t('sellerHub.itemPrice')"
            :value="listingForm.price"
            @input="listingForm.price = $event.target.value.replace(/[^0-9.]/g, '')"
          ><template #media>
            <span class="sel-listing-currency">{{ listingForm.currency || 'XOF' }}</span>
              </template></f7-list-input>
          <f7-list-input
            type="select"
            :label="$t('sellerHub.contactMethod')"
            :value="listingForm.contactMethod"
            @change="listingForm.contactMethod = $event.target.value"
          >
            <option v-for="m in CONTACT_METHODS" :key="m.code" :value="m.code">
              {{ contactLabel(m.code) }}
            </option>
          </f7-list-input>
          <f7-list-input
            v-if="listingForm.contactMethod !== 'CHAT'"
            type="tel"
            inputmode="tel"
            ref="listingPhoneRef"
            :label="$t('sellerHub.contactValue')"
            :value="listingForm.contactValue"
            @input="listingForm.contactValue = $event.target.value"
          />
        </f7-list>
        <f7-block>
          <f7-button large raised round fill @click="saveListing" :disabled="listingSaving">
            {{ $t('sellerHub.saveListing') }}
          </f7-button>
        </f7-block>
      </f7-page-content>
    </AdaptiveSheet>

    <!-- Seller profile popup -->
    <AdaptiveSheet v-model:opened="profileSheetOpen" swipe-to-close :tablet-fullscreen="false" >
      <f7-page-content>
         <div class="card-header">
          <f7-block-title class="text-align-center">{{ $t('sellerHub.profileTitle') }}</f7-block-title>
          <f7-link sheet-close popup-close style="right:8px"><f7-icon ios="f7:multiply" md="material:close" /></f7-link>
        </div>
        <f7-list inset strong media-list outline dividers>
          <f7-list-item :title="$t('sellerHub.sellerType')">
            <template #after>
              <f7-segmented>
                <f7-button
                  small round raised
                  :class="profileForm.sellerType !== 'BUSINESS' ? 'sel-seg-active' : ''"
                  @click="profileForm.sellerType = 'INDIVIDUAL'"
                >
                  {{ $t('sellerHub.sellerTypeIndividual') }}
                </f7-button>
                <f7-button
                  small round raised
                  :class="profileForm.sellerType === 'BUSINESS' ? 'sel-seg-active' : ''"
                  @click="profileForm.sellerType = 'BUSINESS'"
                >
                  {{ $t('sellerHub.sellerTypeBusiness') }}
                </f7-button>
              </f7-segmented>
            </template>
          </f7-list-item>
          <f7-list-input
            type="text"
            :label="$t('sellerHub.displayName')"
            :value="profileForm.displayName"
            @input="profileForm.displayName = $event.target.value"
          />
          <f7-list-input
            v-if="profileForm.sellerType === 'BUSINESS'"
            type="text"
            :label="$t('sellerHub.businessName')"
            :value="profileForm.businessName"
            @input="profileForm.businessName = $event.target.value"
          />
          <f7-list-input
            type="textarea"
            resizable
            :label="$t('sellerHub.description')"
            :value="profileForm.description"
            @input="profileForm.description = $event.target.value"
          />
          <f7-list-input
            type="text"
            :label="$t('sellerHub.location')"
            :value="profileForm.location"
            @input="profileForm.location = $event.target.value"
          />
          <f7-list-input
            type="tel"
            ref="profilePhoneRef"
            inputmode="tel"
            :label="$t('sellerHub.phone')"
            :value="profileForm.phone"
            @input="profileForm.phone = $event.target.value"
          />
          <f7-list-input
            type="text"
            :label="$t('sellerHub.websiteUrl')"
            :value="profileForm.websiteUrl"
            @input="profileForm.websiteUrl = $event.target.value"
          />
        </f7-list>
        <f7-block>
          <f7-button large raised round fill @click="saveProfile" :disabled="profileSaving">
            {{ $t('sellerHub.saveProfile') }}
          </f7-button>
        </f7-block>
      </f7-page-content>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import AdaptiveSheet from "../components/common/AdaptiveSheet.vue";
import { useMarketplaceProductStore } from "../js/pinia-stores/marketplaceProduct.js";
import { useMarketplaceStore } from "../js/pinia-stores/marketplace.js";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import {useAccountStore} from "../js/pinia-stores/account.js";

import { useTelephone } from "../js/composables/useTelephone.js";
import { CONTACT_METHODS } from "../constants/listing-options.js";
import i18n from "../js/i18n.js";

const { t } = i18n.global;
const mpStore = useMarketplaceProductStore();
const marketplaceStore = useMarketplaceStore();
const groupsStore = useGroupsStore();
const keycloakStore = useKeycloakStore();
const accountStore=useAccountStore();
const PROFILE_KEY = "avo_seller_profile";

const loading = ref(true);
const loadError = ref(null);
const listings = ref([]);
// Two separate type="tel" inputs live on this page (listing popup + profile
// popup): each gets its own ref and intl-tel-input instance.
const listingPhoneRef = ref(null);
useTelephone(listingPhoneRef);
const profilePhoneRef = ref(null);
useTelephone(profilePhoneRef);
const profileSaved = ref(null);
function readSavedProfile() {
  try {
    profileSaved.value = JSON.parse(localStorage.getItem(PROFILE_KEY) || "null");
  } catch (e) {
    profileSaved.value = null;
  }
}
readSavedProfile();

const listingSheetOpen = ref(false);
const listingSaving = ref(false);
const listingEditId = ref(null);
const listingForm = reactive({
  title: "",
  description: "",
  price: "",
  currency: "",
  contactMethod: "WHATSAPP",
  contactValue: "",
});
const userAccount=computed(() => accountStore.userAccount);
const profile=computed(()=>keycloakStore.profile);

const address = computed(() => {
  const a = userAccount.value?.standard?.address;
  if (!a) return "";
  return [a.label1, a.label2, a.zip_code, a.city, a.state, a.country]
    .filter(Boolean)
    .join(" ");
});

const profileSheetOpen = ref(false);
const profileSaving = ref(false);
const profileForm = reactive({
  sellerType: "INDIVIDUAL",
  displayName: userAccount.value?.name || profile.value?.name || "",
  businessName: "",
  description: "",
  location: address.value || "",
  phone: userAccount.value?.username || profile.value?.username || "",
  websiteUrl: "",
});

const groups = computed(() => groupsStore.groups || []);
const suggestUpgrade = computed(() =>
  listings.value.some((l) => l.suggestShopUpgrade),
);

const listingSheetTitle = computed(() =>
  listingEditId.value
    ? t("sellerHub.editListing")
    : t("sellerHub.newListing"),
);

function contactLabel(code) {
  return (
    {
      WHATSAPP: t("marketplace.contactWhatsapp"),
      CHAT: t("marketplace.contactChat"),
      PHONE: t("marketplace.contactPhone"),
    }[code] || code
  );
}

function statusLabel(status) {
  return (
    {
      DRAFT: t("sellerHub.statusDraft"),
      PENDING_REVIEW: t("sellerHub.statusPending"),
      ACTIVE: t("sellerHub.statusActive"),
      REJECTED: t("sellerHub.statusRejected"),
      EXPIRED: t("sellerHub.statusExpired"),
      ARCHIVED: t("sellerHub.statusArchived"),
      SOLD: t("sellerHub.statusSold"),
      RESERVED: t("sellerHub.statusReserved"),
    }[status] || status
  );
}

function canSubmit(l) {
  return ["DRAFT", "REJECTED", "EXPIRED"].includes(l.status);
}

function canSubmitToGroup(l) {
  return ["ACTIVE", "PENDING_REVIEW", "DRAFT"].includes(l.status);
}

function go(path) {
  f7.views.main.router.navigate(path);
}

function submissionsByListing(l) {
  return marketplaceStore.groupListings?.[l.id] ? 1 : 0;
}

function cover(l) {
  const m = l.media?.[0];
  return m?.thumbnailUrl || m?.url || "";
}

function formatPrice(l) {
  if (l.price == null || l.price === "") return t("marketplace.priceOnRequest");
  const n = Number(l.price);
  return `${n.toLocaleString()}${l.currency ? " " + l.currency : ""}`;
}

async function load() {
  loading.value = true;
  loadError.value = null;
  try {
    listings.value = await mpStore.myListings();
  } catch (e) {
    listings.value = [];
    loadError.value = e?.message || t("sellerHub.loadError");
  } finally {
    loading.value = false;
  }
  try {
    if (!groupsStore.groups.length) await groupsStore.fetchGroups();
  } catch (e) {
    // directory unavailable
  }
}

function onShow() {
  load();
  readSavedProfile();
}

function openProfileForm() {
  profileForm.sellerType = profileSaved.value?.sellerType || "INDIVIDUAL";
  profileForm.displayName =
    profileSaved.value?.displayName ||
    userAccount.value?.name ||
    profile.value?.name ||  
    keycloakStore.userInfo?.name ||
    keycloakStore.userInfo?.given_name ||
    "";
  profileForm.businessName = profileSaved.value?.businessName || "";
  profileForm.description = profileSaved.value?.description || "";
  profileForm.location = profileSaved.value?.location || address.value ||"";
  profileForm.phone = profileSaved.value?.phone ||userAccount.value?.username || profile.value?.username || "";
  profileForm.websiteUrl = profileSaved.value?.websiteUrl || "";
  profileSheetOpen.value = true;
}

function editProfile() {
  openProfileForm();
}

async function saveProfile() {
  profileSaving.value = true;
  try {
    const payload = {
      sellerType: profileForm.sellerType,
      displayName: profileForm.displayName.trim(),
      businessName: profileForm.businessName.trim(),
      description: profileForm.description.trim(),
      location: profileForm.location.trim(),
      phone: profileForm.phone.trim(),
      websiteUrl: profileForm.websiteUrl.trim(),
    };
    const saved = await mpStore.sellerProfile(payload);
    profileSaved.value = saved || payload;
    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(profileSaved.value));
    } catch (e) {
      // storage unavailable
    }
    profileSheetOpen.value = false;
    f7.toast.show({ text: t("sellerHub.saved"), closeTimeout: 2000 });
  } catch (e) {
    f7.dialog.alert(
      e?.response?.data?.message || e?.message || t("sellerHub.saveError"),
      t("common.error"),
    );
  } finally {
    profileSaving.value = false;
  }
}

function openNewListing() {
  listingEditId.value = null;
  listingForm.title = "";
  listingForm.description = "";
  listingForm.price = "";
  listingForm.currency = userAccount.value?.currencyCode || profile?.value.currencyCode|| "XOF";
  listingForm.contactMethod = "WHATSAPP";
  listingForm.contactValue = profileSaved.value?.phone ||userAccount.value?.username || profile.value?.username || "";
  listingSheetOpen.value = true;
}

function openEdit(l) {
  listingEditId.value = l.id;
  listingForm.title = l.title || "";
  listingForm.description = l.description || "";
  listingForm.price = l.price == null ? "" : String(l.price);
  listingForm.currency = l.currency || userAccount.value?.currencyCode || profile?.value.currencyCode|| "XOF";
  listingForm.contactMethod = l.contactMethod || "WHATSAPP";
  listingForm.contactValue = l.contactValue || "";
  listingSheetOpen.value = true;
}

async function saveListing() {
  const title = listingForm.title.trim();
  if (!title) {
    f7.toast.show({ text: t("marketplace.formRequired"), closeTimeout: 2000 });
    return;
  }
  listingSaving.value = true;
  try {
    const payload = {
      listingType: "PRODUCT",
      fulfillmentType: "CONTACT_SELLER",
      title,
      description: listingForm.description.trim(),
      price: listingForm.price === "" ? null : Number(listingForm.price),
      currency: listingForm.currency || userAccount.value?.currencyCode || profile?.value.currencyCode|| "XOF",
      location: profileSaved.value?.location || address.value || "",
      contactUrl:
        listingForm.contactMethod === "CHAT"
          ? ""
          : listingForm.contactValue.trim(),
      expiresAt: null,
      media: [],
    };
    if (listingEditId.value) {
      await mpStore.updateListing(listingEditId.value, payload);
    } else {
      await mpStore.createListing(payload);
    }
    listingSheetOpen.value = false;
    await load();
    f7.toast.show({ text: t("sellerHub.saved"), closeTimeout: 1500 });
  } catch (e) {
    f7.dialog.alert(
      e?.response?.data?.message || e?.message || t("sellerHub.saveError"),
      t("common.error"),
    );
  } finally {
    listingSaving.value = false;
  }
}

async function submitListing(l) {
  try {
    await mpStore.submitListing(l.id);
    await load();
    f7.toast.show({ text: t("sellerHub.submitted"), closeTimeout: 2000 });
  } catch (e) {
    f7.toast.show({
      text: t("sellerHub.submitError"),
      closeTimeout: 2500,
      color: "red",
    });
  }
}

function goShopWizard() {
  f7.views.main.router.navigate("/shop/create");
}

function goMarketplaceGroup(g) {
  f7.views.main.router.navigate(`/groups/${g.id}/marketplace`, { props: { group: g } });
}

function goMarketplace(l) {
  const gl = marketplaceStore.groupListings?.[l.id];
  if (gl?.groupId) f7.views.main.router.navigate(`/groups/${gl.groupId}/marketplace`, { props: { group: gl } });
}

onMounted(() => {
  load();
});
</script>

<style scoped>
.sel-error {
  display: flex;
  align-items: center;
  gap: 10px;
  background: color-mix(in srgb, var(--f7-theme-color) 10%, transparent);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--f7-theme-color);
  font-size: 13px;
  margin-top: 8px;
}
.sel-profile-card {
  border-radius: 12px;
}
.sel-profile-card--cta {
  background: color-mix(in srgb, var(--f7-theme-color) 8%, transparent);
}
.sel-profile-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.sel-profile-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--f7-list-bg-color, #f0f0f2);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}
.sel-profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.sel-profile-info {
  flex: 1;
  min-width: 0;
}
.sel-profile-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sel-profile-sub {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 2px;
}
.sel-verified {
  color: var(--avocash-green, #1a6b42);
  font-weight: 600;
}
.sel-upgrade-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: color-mix(in srgb, #b25e09 8%, transparent);
  border-radius: 12px;
}
.sel-upgrade-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: color-mix(in srgb, #b25e09 15%, transparent);
  color: #b25e09;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sel-upgrade-body {
  flex: 1;
  min-width: 0;
}
.sel-upgrade-title {
  font-size: 14px;
  font-weight: 600;
  color: #8a4a04;
}
.sel-upgrade-text {
  font-size: 12px;
  color: color-mix(in srgb, #8a4a04 80%, transparent);
  margin-top: 2px;
  line-height: 1.4;
}
.sel-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sel-listing-after {
  text-align: right;
}
.sel-listing-price {
  font-size: 13px;
  font-weight: 600;
  color: var(--avocash-green, #1a6b42);
}
.sel-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  margin-bottom: 4px;
}
.sel-badge--DRAFT { background: color-mix(in srgb, #8e8e93 12%, transparent); color: #636366; }
.sel-badge--PENDING_REVIEW { background: color-mix(in srgb, #b25e09 12%, transparent); color: #b25e09; }
.sel-badge--ACTIVE { background: color-mix(in srgb, #1a6b42 12%, transparent); color: #1a6b42; }
.sel-badge--REJECTED { background: color-mix(in srgb, #c0392b 12%, transparent); color: #c0392b; }
.sel-listing-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  margin-top: 4px;
}
.sel-avatar-ph {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--f7-theme-color);
}
.sel-group-link {
  font-size: 12px;
  color: var(--f7-theme-color);
}
.sel-empty {
  padding: 8px 0 24px;
}
.sel-seg-active {
  background: color-mix(in srgb, var(--f7-theme-color) 14%, transparent);
}
</style>